// React
import DataTable from 'react-data-table-component';
import React, { useCallback, useEffect, useState } from 'react';
import {
  Col,
  Row,
  Dropdown,
  ButtonGroup,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  UncontrolledDropdown,
  UncontrolledButtonDropdown,
} from 'reactstrap';

// Components
import { ModalDelete } from '../../components/modalDelete';

// Styles
import '../../assets/scss/pages/_datatable-search.scss';
// import 'react-data-table-component-extensions/dist/index.css'; => default style of search by dist.

/**
 * @NOTES - (read only if you need to use this component in a module with remote paging)
 *
 * For the implementation of the automatic saving feature of user search filters
 * (search and current paging of the datatable) to work in a table with remote paging,
 * some local adjustments are required to the module in question.
 *
 * See the Associates module at:
 * associates > pages > associatesList > index.jsx
 * for instructions on the respective necessary adjustments.
 *
 */

export const DropdownDatatable = ({
  data,
  title,
  columns,
  loading,
  actions = [],
  handleDelete,
  isBills = false,
  canSearch = true,
  handleStatusSelect,
  hasStatusSelect = false,
  keysSearch = ['name'],

  // required props with you using the datatable in a module with remote paging
  onChangePage,
  currentPageRemote,
  isRemotePagination = false,
  ...rest
}) => {
  let savedSearch = sessionStorage.getItem('search');
  let savedCurrentPage = sessionStorage.getItem('currentPage');

  const [registerId, setRegisterId] = useState(null);
  const [filteredData, setFilteredData] = useState([]);
  const [modal_delete, setModal_delete] = useState(false);
  const [search, setSearch] = useState(savedSearch || '');
  const [currentStatus, setCurrentStatus] = useState('Ativos');
  const [currentPage, setCurrentPage] = useState(Number(savedCurrentPage) || 1);

  const handlePageChange = (page) => {
    setCurrentPage(page);
    sessionStorage.setItem('currentPage', page);
  };

  const toggleModal = useCallback(
    (id) => {
      setRegisterId(id);
      setModal_delete(!modal_delete);
    },
    [modal_delete]
  );

  const createColumnsWithActionsData = useCallback(
    (cellProps) => {
      let columns = (actions || []).map((item) => {
        return (
          <DropdownItem
            key={item.id}
            title={item.label}
            className="dropdown-item"
            onClick={() => {
              item.onPress(cellProps);
            }}
          >
            <i
              className={`las ${item.icon} fs-20 text-${item.classNameColorIcon}`}
            ></i>
            {item.label}
          </DropdownItem>
        );
      });

      return columns;
    },
    [actions, toggleModal]
  );

  const normalizeItem = (item, event) => {
    return item
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^\w\s]|_/g, '')
      .toLowerCase()
      .trim()
      .includes(
        event
          .toLowerCase()
          .normalize('NFD')
          .replace(/[\u0300-\u036f]/g, '')
          .replace(/[^\w\s]|_/g, '')
          .trim()
      );
  };

  const handleSearchLocal = (event) => {
    setSearch(event);

    sessionStorage.setItem('search', event);

    if (event === null) {
      setFilteredData([]);
      return;
    }

    let newData = [];

    (keysSearch || []).map((key) => {
      let newArr = data.filter((item) => {
        if (item[key]) {
          return normalizeItem(item[key], event);
        }
      });
      newData = newData.concat(
        newArr.filter((item) => newData.indexOf(item) < 0)
      );
    });

    setFilteredData(newData);
  };

  const NoDataComponent = () => {
    return <p className="m-4">Nenhum registro encontrado</p>;
  };

  /**
   * @NOTES
   *
   * Currently, this component is being used in several project modules, and the user's search
   * filters (search and current table page) must only be maintained if the user navigates between
   * pages in the same modules (from the list to edit a record, for example).
   *
   * If it navigates from one module to another, the filters must be reset --> (usePath hook responsible for this).
   *
   * These useEffects below ensures that sessionStorage is cleared when user is changing modules that use the
   * same component - (when rendering this component for the first time, the `savedCurrentPage` state
   * still rescues the sessionStorage `search` before being cleared by the usePathname hook).
   *
   * The same happens with the ´savedSearch´ state.
   *
   * So basically these useEffects are responsible for monitoring the existence of data in sessionStorage
   * to be retrieved and maintained as user search filters, if the user is browsing between pages of the
   * same module.
   *
   */
  useEffect(() => {
    if (!savedSearch) {
      setSearch('');
      setFilteredData(data);
    } else {
      handleSearchLocal(savedSearch);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [savedSearch, data]);

  useEffect(() => {
    if (!savedCurrentPage) {
      setCurrentPage(1);
    }
  }, [savedCurrentPage]);

  return (
    <React.Fragment>
      {hasStatusSelect && (
        <>
          <div
            style={{
              paddingTop: '15px',
              paddingRight: '20px',
            }}
          >
            <div className="text-end d-flex justify-content-end">
              <span
                className="fw-bold text-dark"
                style={{
                  marginRight: 10,
                }}
              >
                EXIBIR:
              </span>
              <ButtonGroup>
                <UncontrolledDropdown>
                  <DropdownToggle
                    tag={'div'}
                    className="text-muted user-select-none"
                    style={{
                      cursor: 'pointer',
                    }}
                  >
                    {currentStatus} <i className="mdi mdi-chevron-down"></i>
                  </DropdownToggle>
                  <DropdownMenu>
                    <DropdownItem
                      value="true"
                      defaultValue
                      onClick={(e) => {
                        setCurrentStatus('Ativos');
                        handleStatusSelect(e);
                      }}
                    >
                      Ativos
                    </DropdownItem>
                    <DropdownItem divider />
                    <DropdownItem
                      value="false"
                      onClick={(e) => {
                        setCurrentStatus('Inativos');
                        handleStatusSelect(e);
                      }}
                    >
                      Inativos
                    </DropdownItem>
                  </DropdownMenu>
                </UncontrolledDropdown>
              </ButtonGroup>
            </div>
          </div>
        </>
      )}

      {canSearch ? (
        <div style={hasStatusSelect ? { marginTop: -25 } : { marginTop: 15 }}>
          <Row className="g-3 ms-2">
            <Col xl={4} lg={4} md={4} sm={8} xs={8} className="pe-5">
              <div className="search-box">
                <input
                  type="text"
                  id="search"
                  className="form-control search /"
                  placeholder="Pesquisar..."
                  onChange={(e) => {
                    handleSearchLocal(e.target.value);
                    setCurrentPage(1);
                  }}
                  value={search}
                />
                <i className="ri-search-line search-icon"></i>
              </div>
            </Col>
          </Row>
        </div>
      ) : null}

      <DataTable
        {...rest}
        data={filteredData.length ? filteredData : []}
        loading={true}
        columns={[
          ...columns,
          {
            name: <span className="font-weight-bold fs-13"> </span>,
            sortable: false,
            style: {
              justifyContent: 'end',
            },
            cell: (cellProps) => {
              return (
                <Dropdown>
                  <UncontrolledButtonDropdown>
                    <DropdownToggle
                      hidden={
                        cellProps.can_delete === false ||
                        (isBills && cellProps.status.title === 'Pago')
                      }
                      nav
                      className="las la-ellipsis-v text-primary fs-18"
                    ></DropdownToggle>
                    <div
                      style={{
                        position: 'fixed',
                        zIndex: '999',
                      }}
                    >
                      <DropdownMenu>
                        {createColumnsWithActionsData(cellProps)}
                        {handleDelete && (
                          <>
                            {actions.length > 0 && <DropdownItem divider />}

                            <DropdownItem
                              className="dropdown-item"
                              onClick={() => {
                                toggleModal(cellProps.id);
                              }}
                            >
                              <i className="las la-trash fs-20 text-danger" />
                              Excluir
                            </DropdownItem>
                          </>
                        )}
                      </DropdownMenu>
                    </div>
                  </UncontrolledButtonDropdown>
                </Dropdown>
              );
            },
          },
        ]}
        paginationComponentOptions={{
          rowsPerPageText: 'Linhas por página:',
          rangeSeparatorText: 'de',
          selectAllRowsItem: true,
          selectAllRowsItemText: 'Todos',
        }}
        pagination
        paginationPerPage={10}
        paginationDefaultPage={
          isRemotePagination ? currentPageRemote : currentPage
        }
        onChangePage={
          onChangePage ? onChangePage : (page) => handlePageChange(page)
        }
        noDataComponent={<NoDataComponent />}
      />

      <ModalDelete
        isOpen={modal_delete}
        handleDelete={() => {
          handleDelete(registerId);
          setModal_delete(!modal_delete);
        }}
        goBack={() => setModal_delete(!modal_delete)}
      />
    </React.Fragment>
  );
};
